/**
 * Context Window Optimizer
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Smart context selection that maximizes relevant code in limited token budget
 * - Prioritizes definitions, types, and usage patterns that matter
 * - Uses semantic similarity and dependency graphs
 * - Adaptive chunking based on code structure
 */

import * as fs from 'fs';
import * as path from 'path';
import {
  ContextChunk,
  OptimizedContext,
  ContextCoverage,
  CodebaseKnowledge,
} from './types';

export interface ContextRequest {
  targetFile: string;
  targetLine?: number;
  intent: string;           // What the user is trying to do
  maxTokens: number;        // Token budget
  preferredTypes?: ContextChunk['type'][];
}

interface FileAnalysis {
  filePath: string;
  imports: string[];
  exports: string[];
  types: string[];
  functions: string[];
  dependencies: string[];
  relevanceScore: number;
}

export class ContextOptimizer {
  private workspaceRoot: string;
  private knowledge: CodebaseKnowledge | null = null;
  private fileAnalysisCache: Map<string, FileAnalysis> = new Map();
  private avgCharsPerToken = 4; // Rough estimate

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
  }

  /**
   * Optimize context selection for a request
   */
  async optimize(request: ContextRequest): Promise<OptimizedContext> {
    const chunks: ContextChunk[] = [];
    let totalTokens = 0;
    const tokenBudget = request.maxTokens;

    // Phase 1: Analyze target file and immediate context
    const targetAnalysis = await this.analyzeFile(request.targetFile);
    
    // Phase 2: Build relevance-scored chunk candidates
    const candidates = await this.buildCandidates(request, targetAnalysis);
    
    // Phase 3: Greedy selection with diversity
    const selectedChunks = this.greedySelect(candidates, tokenBudget, request.preferredTypes);
    
    // Phase 4: Calculate coverage metrics
    const coverage = this.calculateCoverage(selectedChunks, targetAnalysis);

    return {
      chunks: selectedChunks,
      totalTokens: selectedChunks.reduce((sum, c) => sum + c.tokens, 0),
      coverage,
      strategy: this.describeStrategy(request, selectedChunks),
    };
  }

  /**
   * Analyze a file for imports, exports, types, functions
   */
  private async analyzeFile(filePath: string): Promise<FileAnalysis> {
    const cached = this.fileAnalysisCache.get(filePath);
    if (cached) return cached;

    const fullPath = path.isAbsolute(filePath) 
      ? filePath 
      : path.join(this.workspaceRoot, filePath);

    if (!fs.existsSync(fullPath)) {
      return {
        filePath,
        imports: [],
        exports: [],
        types: [],
        functions: [],
        dependencies: [],
        relevanceScore: 0,
      };
    }

    const content = fs.readFileSync(fullPath, 'utf-8');
    
    const analysis: FileAnalysis = {
      filePath,
      imports: this.extractImports(content),
      exports: this.extractExports(content),
      types: this.extractTypes(content),
      functions: this.extractFunctions(content),
      dependencies: [],
      relevanceScore: 1.0,
    };

    // Build dependency list from imports
    analysis.dependencies = analysis.imports.map(imp => {
      if (imp.startsWith('.')) {
        return path.resolve(path.dirname(fullPath), imp);
      }
      return imp;
    });

    this.fileAnalysisCache.set(filePath, analysis);
    return analysis;
  }

  /**
   * Build candidate chunks with relevance scores
   */
  private async buildCandidates(
    request: ContextRequest,
    targetAnalysis: FileAnalysis
  ): Promise<ContextChunk[]> {
    const candidates: ContextChunk[] = [];
    const visited = new Set<string>();

    // 1. Target file context (highest priority)
    const targetChunks = await this.chunkFile(request.targetFile, request.targetLine);
    for (const chunk of targetChunks) {
      chunk.relevanceScore = this.scoreRelevance(chunk, request, targetAnalysis, 1.5);
      candidates.push(chunk);
    }
    visited.add(request.targetFile);

    // 2. Direct dependencies (imports)
    for (const dep of targetAnalysis.dependencies) {
      if (visited.has(dep) || !dep.startsWith('.') && !dep.startsWith('/')) continue;
      
      const depChunks = await this.chunkFile(dep);
      for (const chunk of depChunks) {
        chunk.relevanceScore = this.scoreRelevance(chunk, request, targetAnalysis, 1.2);
        candidates.push(chunk);
      }
      visited.add(dep);
    }

    // 3. Type definition files
    const typeFiles = await this.findTypeFiles(targetAnalysis.types);
    for (const typeFile of typeFiles) {
      if (visited.has(typeFile)) continue;
      
      const typeChunks = await this.chunkFile(typeFile);
      for (const chunk of typeChunks) {
        chunk.type = 'type';
        chunk.relevanceScore = this.scoreRelevance(chunk, request, targetAnalysis, 1.1);
        candidates.push(chunk);
      }
      visited.add(typeFile);
    }

    // 4. Related test files
    const testFile = this.findTestFile(request.targetFile);
    if (testFile && !visited.has(testFile)) {
      const testChunks = await this.chunkFile(testFile);
      for (const chunk of testChunks) {
        chunk.type = 'test';
        chunk.relevanceScore = this.scoreRelevance(chunk, request, targetAnalysis, 0.8);
        candidates.push(chunk);
      }
      visited.add(testFile);
    }

    // 5. Usage examples (files that import target)
    const usageFiles = await this.findUsageFiles(request.targetFile);
    for (const usageFile of usageFiles.slice(0, 3)) {
      if (visited.has(usageFile)) continue;
      
      const usageChunks = await this.chunkFile(usageFile);
      for (const chunk of usageChunks) {
        chunk.type = 'usage';
        chunk.relevanceScore = this.scoreRelevance(chunk, request, targetAnalysis, 0.7);
        candidates.push(chunk);
      }
      visited.add(usageFile);
    }

    // 6. Config files (if relevant)
    if (request.intent.toLowerCase().includes('config') || 
        request.intent.toLowerCase().includes('env') ||
        request.intent.toLowerCase().includes('setup')) {
      const configChunks = await this.getConfigChunks();
      for (const chunk of configChunks) {
        chunk.type = 'config';
        chunk.relevanceScore = 0.6;
        candidates.push(chunk);
      }
    }

    return candidates;
  }

  /**
   * Chunk a file into semantic segments
   */
  private async chunkFile(filePath: string, focusLine?: number): Promise<ContextChunk[]> {
    const fullPath = path.isAbsolute(filePath) 
      ? filePath 
      : path.join(this.workspaceRoot, filePath);

    if (!fs.existsSync(fullPath)) {
      return [];
    }

    const content = fs.readFileSync(fullPath, 'utf-8');
    const lines = content.split('\n');
    const chunks: ContextChunk[] = [];

    // Strategy: Chunk by logical boundaries
    let currentChunk: { start: number; end: number; type: ContextChunk['type'] } | null = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineNum = i + 1;

      // Detect chunk boundaries
      const isImport = /^import\s/.test(line);
      const isExport = /^export\s/.test(line);
      const isFunctionStart = /^(?:export\s+)?(?:async\s+)?function\s+\w+/.test(line) ||
                              /^(?:export\s+)?const\s+\w+\s*=\s*(?:async\s*)?\(/.test(line) ||
                              /^(?:export\s+)?const\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>/.test(line);
      const isClassStart = /^(?:export\s+)?class\s+\w+/.test(line);
      const isInterfaceStart = /^(?:export\s+)?(?:interface|type)\s+\w+/.test(line);
      const isEmptyLine = line.trim() === '';

      // Determine chunk type
      let chunkType: ContextChunk['type'] = 'usage';
      if (isImport) chunkType = 'import';
      else if (isInterfaceStart) chunkType = 'type';
      else if (isFunctionStart || isClassStart) chunkType = 'definition';

      // Start new chunk on boundaries
      if (isImport || isFunctionStart || isClassStart || isInterfaceStart) {
        if (currentChunk) {
          chunks.push(this.createChunk(filePath, lines, currentChunk.start, currentChunk.end, currentChunk.type));
        }
        currentChunk = { start: lineNum, end: lineNum, type: chunkType };
      } else if (currentChunk) {
        // Continue current chunk
        currentChunk.end = lineNum;
        
        // End chunk on double newline or closing brace at indent 0
        if ((isEmptyLine && i > 0 && lines[i - 1].trim() === '') ||
            (line.trim() === '}' && !line.startsWith(' ') && !line.startsWith('\t'))) {
          chunks.push(this.createChunk(filePath, lines, currentChunk.start, currentChunk.end, currentChunk.type));
          currentChunk = null;
        }
      }
    }

    // Don't forget last chunk
    if (currentChunk) {
      chunks.push(this.createChunk(filePath, lines, currentChunk.start, currentChunk.end, currentChunk.type));
    }

    // Boost relevance of chunks near focus line
    if (focusLine) {
      for (const chunk of chunks) {
        const distance = Math.min(
          Math.abs(chunk.startLine - focusLine),
          Math.abs(chunk.endLine - focusLine)
        );
        if (distance === 0) {
          chunk.relevanceScore *= 2.0;
        } else if (distance < 10) {
          chunk.relevanceScore *= 1.5;
        } else if (distance < 30) {
          chunk.relevanceScore *= 1.2;
        }
      }
    }

    return chunks;
  }

  /**
   * Create a context chunk
   */
  private createChunk(
    filePath: string,
    lines: string[],
    start: number,
    end: number,
    type: ContextChunk['type']
  ): ContextChunk {
    const content = lines.slice(start - 1, end).join('\n');
    const tokens = Math.ceil(content.length / this.avgCharsPerToken);

    return {
      id: `${filePath}:${start}-${end}`,
      filePath,
      content,
      startLine: start,
      endLine: end,
      relevanceScore: 1.0,
      tokens,
      type,
    };
  }

  /**
   * Score chunk relevance based on multiple factors
   */
  private scoreRelevance(
    chunk: ContextChunk,
    request: ContextRequest,
    targetAnalysis: FileAnalysis,
    baseMultiplier: number
  ): number {
    let score = baseMultiplier;

    // Boost for matching intent keywords
    const intentWords = request.intent.toLowerCase().split(/\s+/);
    const chunkLower = chunk.content.toLowerCase();
    for (const word of intentWords) {
      if (word.length > 3 && chunkLower.includes(word)) {
        score *= 1.2;
      }
    }

    // Boost for containing referenced types
    for (const type of targetAnalysis.types) {
      if (chunk.content.includes(type)) {
        score *= 1.3;
      }
    }

    // Boost for containing referenced functions
    for (const func of targetAnalysis.functions) {
      if (chunk.content.includes(func)) {
        score *= 1.2;
      }
    }

    // Penalty for very long chunks (likely too general)
    if (chunk.tokens > 500) {
      score *= 0.7;
    }

    // Boost for type definitions
    if (chunk.type === 'type') {
      score *= 1.1;
    }

    // Boost for definitions over usage
    if (chunk.type === 'definition') {
      score *= 1.15;
    }

    return score;
  }

  /**
   * Greedy selection with diversity
   */
  private greedySelect(
    candidates: ContextChunk[],
    tokenBudget: number,
    preferredTypes?: ContextChunk['type'][]
  ): ContextChunk[] {
    // Sort by relevance score
    const sorted = [...candidates].sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    const selected: ContextChunk[] = [];
    let usedTokens = 0;
    const selectedTypes = new Set<ContextChunk['type']>();
    const selectedFiles = new Set<string>();

    for (const chunk of sorted) {
      if (usedTokens + chunk.tokens > tokenBudget) {
        continue;
      }

      // Diversity bonus: prefer chunks from different files/types
      let diversityBonus = 1.0;
      if (!selectedFiles.has(chunk.filePath)) {
        diversityBonus *= 1.2;
      }
      if (!selectedTypes.has(chunk.type)) {
        diversityBonus *= 1.1;
      }

      // Preferred types bonus
      if (preferredTypes && preferredTypes.includes(chunk.type)) {
        diversityBonus *= 1.3;
      }

      // Apply diversity bonus to effective score
      const effectiveScore = chunk.relevanceScore * diversityBonus;

      // Check if this chunk is worth including
      if (effectiveScore > 0.3 || selected.length < 5) {
        selected.push(chunk);
        usedTokens += chunk.tokens;
        selectedTypes.add(chunk.type);
        selectedFiles.add(chunk.filePath);
      }
    }

    return selected;
  }

  /**
   * Calculate context coverage metrics
   */
  private calculateCoverage(chunks: ContextChunk[], targetAnalysis: FileAnalysis): ContextCoverage {
    const allContent = chunks.map(c => c.content).join('\n');
    
    // Definition coverage
    const definitionChunks = chunks.filter(c => c.type === 'definition');
    const definitions = targetAnalysis.functions.length + targetAnalysis.exports.length;
    const coveredDefinitions = definitionChunks.length;

    // Type coverage
    const typeChunks = chunks.filter(c => c.type === 'type');
    const coveredTypes = targetAnalysis.types.filter(t => allContent.includes(t)).length;

    // Usage coverage
    const usageChunks = chunks.filter(c => c.type === 'usage');
    
    // Test coverage
    const testChunks = chunks.filter(c => c.type === 'test');

    return {
      definitions: definitions > 0 ? Math.round((coveredDefinitions / Math.max(definitions, 1)) * 100) : 100,
      types: targetAnalysis.types.length > 0 ? Math.round((coveredTypes / targetAnalysis.types.length) * 100) : 100,
      usages: Math.min(100, usageChunks.length * 33), // Max 3 usage examples = 100%
      tests: testChunks.length > 0 ? 100 : 0,
    };
  }

  /**
   * Describe the optimization strategy used
   */
  private describeStrategy(request: ContextRequest, chunks: ContextChunk[]): string {
    const typeBreakdown = chunks.reduce((acc, c) => {
      acc[c.type] = (acc[c.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const fileCount = new Set(chunks.map(c => c.filePath)).size;
    
    return `Selected ${chunks.length} chunks from ${fileCount} files: ` +
           Object.entries(typeBreakdown)
             .map(([type, count]) => `${count} ${type}`)
             .join(', ');
  }

  /**
   * Helper: Extract imports from content
   */
  private extractImports(content: string): string[] {
    const imports: string[] = [];
    const importRegex = /import\s+.*?\s+from\s+['"]([^'"]+)['"]/g;
    let match;
    while ((match = importRegex.exec(content)) !== null) {
      imports.push(match[1]);
    }
    return imports;
  }

  /**
   * Helper: Extract exports from content
   */
  private extractExports(content: string): string[] {
    const exports: string[] = [];
    const exportRegex = /export\s+(?:const|let|var|function|class|interface|type|enum)\s+(\w+)/g;
    let match;
    while ((match = exportRegex.exec(content)) !== null) {
      exports.push(match[1]);
    }
    return exports;
  }

  /**
   * Helper: Extract type references
   */
  private extractTypes(content: string): string[] {
    const types: string[] = [];
    const typeRegex = /(?:interface|type)\s+(\w+)/g;
    const usageRegex = /:\s*(\w+)(?:<|>|\[|\]|\s|,|;|\))/g;
    
    let match;
    while ((match = typeRegex.exec(content)) !== null) {
      types.push(match[1]);
    }
    while ((match = usageRegex.exec(content)) !== null) {
      if (!['string', 'number', 'boolean', 'void', 'any', 'unknown', 'never'].includes(match[1])) {
        types.push(match[1]);
      }
    }
    return [...new Set(types)];
  }

  /**
   * Helper: Extract function names
   */
  private extractFunctions(content: string): string[] {
    const functions: string[] = [];
    const funcRegex = /(?:function|const|let|var)\s+(\w+)\s*(?:=\s*(?:async\s*)?\(|\()/g;
    let match;
    while ((match = funcRegex.exec(content)) !== null) {
      functions.push(match[1]);
    }
    return functions;
  }

  /**
   * Find files containing type definitions
   */
  private async findTypeFiles(types: string[]): Promise<string[]> {
    const typeFiles: string[] = [];
    const typeDirs = ['types', 'interfaces', '@types', 'src/types'];

    for (const dir of typeDirs) {
      const fullDir = path.join(this.workspaceRoot, dir);
      if (fs.existsSync(fullDir)) {
        const files = fs.readdirSync(fullDir).filter(f => f.endsWith('.ts') || f.endsWith('.d.ts'));
        for (const file of files) {
          const filePath = path.join(dir, file);
          const content = fs.readFileSync(path.join(fullDir, file), 'utf-8');
          
          for (const type of types) {
            if (content.includes(`interface ${type}`) || content.includes(`type ${type}`)) {
              if (!typeFiles.includes(filePath)) {
                typeFiles.push(filePath);
              }
            }
          }
        }
      }
    }

    return typeFiles;
  }

  /**
   * Find test file for a source file
   */
  private findTestFile(sourceFile: string): string | null {
    const baseName = path.basename(sourceFile, path.extname(sourceFile));
    const dirName = path.dirname(sourceFile);
    
    const testPatterns = [
      `${baseName}.test.ts`,
      `${baseName}.spec.ts`,
      `${baseName}.test.tsx`,
      `${baseName}.spec.tsx`,
      `__tests__/${baseName}.ts`,
      `__tests__/${baseName}.tsx`,
    ];

    for (const pattern of testPatterns) {
      const testPath = path.join(dirName, pattern);
      const fullPath = path.join(this.workspaceRoot, testPath);
      if (fs.existsSync(fullPath)) {
        return testPath;
      }
    }

    return null;
  }

  /**
   * Find files that import the target file
   */
  private async findUsageFiles(targetFile: string): Promise<string[]> {
    const usageFiles: string[] = [];
    const targetName = path.basename(targetFile, path.extname(targetFile));
    
    // Simple search in src directory
    const searchDir = path.join(this.workspaceRoot, 'src');
    if (!fs.existsSync(searchDir)) return usageFiles;

    const searchFiles = (dir: string) => {
      const items = fs.readdirSync(dir, { withFileTypes: true });
      for (const item of items) {
        const fullPath = path.join(dir, item.name);
        if (item.isDirectory() && !item.name.startsWith('.') && item.name !== 'node_modules') {
          searchFiles(fullPath);
        } else if (item.isFile() && (item.name.endsWith('.ts') || item.name.endsWith('.tsx'))) {
          const content = fs.readFileSync(fullPath, 'utf-8');
          if (content.includes(`from './${targetName}'`) || 
              content.includes(`from '../${targetName}'`) ||
              content.includes(`from '${targetFile}'`)) {
            usageFiles.push(path.relative(this.workspaceRoot, fullPath));
          }
        }
      }
    };

    try {
      searchFiles(searchDir);
    } catch {
      // Ignore errors during search
    }

    return usageFiles.slice(0, 5); // Limit to 5 files
  }

  /**
   * Get config file chunks
   */
  private async getConfigChunks(): Promise<ContextChunk[]> {
    const configFiles = [
      'package.json',
      'tsconfig.json',
      '.env.example',
      'vite.config.ts',
      'next.config.js',
      'webpack.config.js',
    ];

    const chunks: ContextChunk[] = [];
    
    for (const configFile of configFiles) {
      const fullPath = path.join(this.workspaceRoot, configFile);
      if (fs.existsSync(fullPath)) {
        const content = fs.readFileSync(fullPath, 'utf-8');
        const tokens = Math.ceil(content.length / this.avgCharsPerToken);
        
        if (tokens < 500) { // Only include if reasonable size
          chunks.push({
            id: `config:${configFile}`,
            filePath: configFile,
            content,
            startLine: 1,
            endLine: content.split('\n').length,
            relevanceScore: 0.5,
            tokens,
            type: 'config',
          });
        }
      }
    }

    return chunks;
  }

  /**
   * Update codebase knowledge
   */
  updateKnowledge(knowledge: CodebaseKnowledge): void {
    this.knowledge = knowledge;
  }

  /**
   * Clear file analysis cache
   */
  clearCache(): void {
    this.fileAnalysisCache.clear();
  }
}
